import { FormInput } from "~/screens/Scheduling/PersonalInformation/components/form-input";
import { CancelReason } from "~/services";
import { LateCancellationFeeCard } from "./LateCancellationFeeCard";

interface CancellationReasonFieldsProps {
  reasons: CancelReason[] | undefined;
  subReasons: CancelReason["children"];
  parentId: string;
  childId: string;
  notes: string;
  waiveFee: boolean;
  showFeeWaiver: boolean;
  reasonEditable?: boolean;
  reasonName?: string | null;
  errors: {
    parentId?: string;
    childId?: string;
    notes?: string;
  };
  onParentChange: (value: string) => void;
  onChildChange: (value: string) => void;
  onChildBlur: () => void;
  onNotesChange: (value: string) => void;
  onWaiveFeeChange: (waiveFee: boolean) => void;
}

/**
 * The reason, sub-reason, notes and fee waiver block, shared by the cancel and the edit
 * modals. Presentational: every value and handler arrives by prop.
 */
export const CancellationReasonFields = ({
  reasons,
  subReasons,
  parentId,
  childId,
  notes,
  waiveFee,
  showFeeWaiver,
  reasonEditable = true,
  reasonName,
  errors,
  onParentChange,
  onChildChange,
  onChildBlur,
  onNotesChange,
  onWaiveFeeChange,
}: CancellationReasonFieldsProps) => (
  <>
    {reasonEditable ? (
      <>
        <FormInput
          label="Select a reason for cancelling"
          name="cancelReasonParentId"
          type="select"
          value={parentId}
          onChange={onParentChange}
          options={reasons?.map((reason) => ({
            value: reason.id.toString(),
            label: reason.name,
          }))}
          required
          error={errors.parentId}
        />

        {subReasons.length > 0 && (
          <FormInput
            label="Patient's reason"
            name="cancelReasonChildId"
            type="select"
            value={childId}
            onChange={onChildChange}
            onBlur={onChildBlur}
            options={subReasons.map((subReason) => ({
              value: subReason.id.toString(),
              label: subReason.name,
            }))}
            required
            error={errors.childId}
          />
        )}
      </>
    ) : (
      /**
       * Read as text rather than a disabled dropdown. A patient chose this reason from the
       * patient-facing list, which the admin dropdown does not load, so a select would find no
       * matching option and fall back to its placeholder. The stored name is also the only
       * thing that keeps rendering once a reason is retired from the list altogether.
       */
      <div className="space-y-2">
        <p className="block font-sans text-sm text-gray-900">
          Reason for cancelling
        </p>
        <p className="text-dark-blue-gray text-base">{reasonName || "-"}</p>
      </div>
    )}

    <FormInput
      label="Cancellation notes"
      name="cancellationNotes"
      type="textarea"
      value={notes}
      onChange={onNotesChange}
      error={errors.notes}
    />

    {showFeeWaiver && (
      <LateCancellationFeeCard
        waiveFee={waiveFee}
        onWaiveFeeChange={onWaiveFeeChange}
      />
    )}
  </>
);
